import React from "react";
import { GSForm, GSSidePanelPage } from "golfstatus_react_components";
import {
  faChevronLeft,
  faCircleArrowDown,
  faCreditCard,
  faExclamationCircle,
  faFileInvoice,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";

//import scss
import "./tournament-reporting-order.scss";
import { useFormValidation } from "../../../hooks/formHooks";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  clearNotifications,
  getOrder,
  resendReceipt,
  resendInvoice,
  selectNotifications,
  markInvoiceAsPaid,
  downloadCustomerInvoice,
  selectOrderSummary,
  selectLoading,
  downloadRegistrationOrderReceipt,
  selectCurrentOrder,
} from "../../../reducers/reportingSlice";
import { getReportingForm } from "../../../forms/ReportingForm";
import { useSelector } from "react-redux";
import { getNotificationItemInfo } from "../../../helpers/Converters";
import { customerInvoicePaymentsApp } from "../../../app/api";

//Name the component
const TournamentReportingOrder = (props) => {
  const [context] = useFormValidation(false);

  const match = useMatch("/:tournamentID/reporting/:orderID");

  const dispatch = useDispatch();

  useEffect(() => {
    if (match?.params?.orderID) {
      dispatch(getOrder(match.params.orderID));
    }
    return () => {
      dispatch(clearNotifications());
    };
  }, [dispatch, match?.params?.orderID]);

  const navigate = useNavigate();
  const currentOrder = useSelector(selectCurrentOrder);
  const notifications = useSelector(selectNotifications);
  const tournamentEventOrderSummary = useSelector(selectOrderSummary);
  const loading = useSelector(selectLoading);

  const availableFunds = tournamentEventOrderSummary?.availableFunds;
  const escrow = tournamentEventOrderSummary?.escrow;
  const refundAmount = currentOrder?.netProfit;
  const isRefund = !currentOrder?.voidable;
  const orderNotRefundedAlready =
    currentOrder?.registrationOrderRefunds?.length < 1;

  const disableRefundFunctionality =
    isRefund &&
    escrow &&
    orderNotRefundedAlready &&
    availableFunds < refundAmount;

  const refOrVoid = currentOrder?.voidable ? "Void" : "Refund";

  const save = () => {
    if (currentOrder?.voidable) {
      navigate("void-order");
    } else {
      navigate("refund");
    }
  };

  const voidInvoice = () => {
    navigate("void-invoice");
  };

  const markAsPaid = () => {
    dispatch(markInvoiceAsPaid(match?.params?.orderID));
  };

  const downloadInvoice = () => {
    dispatch(downloadCustomerInvoice(match?.params?.orderID));
  };

  const downloadOrderReceipt = () => {
    dispatch(downloadRegistrationOrderReceipt(match?.params?.orderID));
  };

  const viewInvoiceInNewTab = () => {
    window.open(customerInvoicePaymentsApp(match?.params?.orderID), "_blank");
  };

  const getDrawerActions = () => {
    if (currentOrder?.registrationOrderRefunds?.length > 0) {
      return [];
    }
    let actions = [];
    if (currentOrder?.status === "sent") {
      actions = [
        {
          name: "Mark as Paid",
          action: markAsPaid,
          type: "green",
        },
        {
          name: "Void Invoice",
          action: voidInvoice,
          type: "light-grey",
        },
      ];
    } else if (currentOrder?.paymentType !== "customer_invoice") {
      actions = [
        {
          name: `${refOrVoid} Order`,
          action: save,
          type: "light-grey",
          isDisabled: disableRefundFunctionality,
        },
      ];
    }

    return actions;
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Order Details`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const isCustomerInvoice = () => {
    return (
      currentOrder?.paymentType === "customer_invoice" ||
      currentOrder?.invoicePaidAt
    );
  };

  const isPaidOrder = () => {
    return (
      (currentOrder?.paymentType === "credit_card" ||
        currentOrder?.paymentType === "bank_account") &&
      currentOrder?.status === "paid" &&
      !currentOrder.invoicePaidAt
    );
  };

  const getFormTitle = () => {
    let actions = [];
    if (isCustomerInvoice()) {
      actions.push({
        buttonTitle: "View Invoice",
        type: "no-wrap light-grey",
        actionClick: viewInvoiceInNewTab,
        actionIcon: faFileInvoice,
      });
    }
    actions.push({
      buttonTitle: `Resend ${isCustomerInvoice() ? "Invoice" : "Receipt"}`,
      type: "no-wrap light-grey",
      actionClick: () => {
        dispatch(
          isCustomerInvoice()
            ? resendInvoice(currentOrder.id)
            : resendReceipt(currentOrder.id)
        );
      },
      actionIcon: faPaperPlane,
    });
    if (isCustomerInvoice()) {
      actions.push({
        type: "no-wrap light-grey",
        actionClick: downloadInvoice,
        actionIcon: faCircleArrowDown,
      });
    }
    if (isPaidOrder()) {
      actions.push({
        type: "no-wrap light-grey",
        actionClick: downloadOrderReceipt,
        actionIcon: faCircleArrowDown,
      });
    }
    return {
      formTitle: "Order Details",
      formActions: actions,
    };
  };

  const getContent = () => {
    return (
      <GSForm
        {...getFormTitle()}
        formSections={getReportingForm(context, currentOrder)}
      ></GSForm>
    );
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const getDefaultBanner = () => {
    if (currentOrder?.voidable) {
      return {
        title: getNotificationItemInfo(
          "Voiding this order will cancel the transaction and the credit card will not be charged.",
          faCreditCard,
          "Pending Credit Card Charge"
        ),
        state: "grey",
      };
    }
    if (disableRefundFunctionality) {
      return {
        title: getNotificationItemInfo(
          "The order is not eligible for a refund, due to insufficient available funds.",
          faExclamationCircle,
          "Non-Refundable Order"
        ),
        content: "body text",
        state: "grey",
      };
    }
  };

  let notificationSettings = {
    notifications,
    timeoutAction,
    defaultBanner: getDefaultBanner(),
  };

  const [banner] = useNotificationBanner(notificationSettings);

  return (
    //name the component tag
    <tournament-reporting-order>
      <GSSidePanelPage
        header={getNavigation()}
        loading={loading.length > 0}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </tournament-reporting-order>
  );
};

//export the new name
export default TournamentReportingOrder;
